import React, { useEffect } from 'react';
import { Box, TextField, IconButton, Paper, CircularProgress, Typography } from '@mui/material';
import { Send as SendIcon, Mic as MicIcon, Stop as StopIcon } from '@mui/icons-material';
import { useVoiceInput } from '../hooks/useVoiceInput';

interface MessageInputProps {
	value: string;
	onChange: (value: string) => void;
	onSubmit: () => void;
	disabled?: boolean;
	placeholder?: string;
	containerAppUrl?: string | null;
}

export const MessageInput: React.FC<MessageInputProps> = ({
	value,
	onChange,
	onSubmit,
	disabled = false,
	placeholder = 'Type your message here...',
	containerAppUrl,
}) => {
	const { isRecording, isTranscribing, error, startRecording, stopRecording, isSupported } =
		useVoiceInput({
			containerAppUrl,
			onTranscript: (text) => onChange(value ? `${value} ${text}` : text),
		});

	// Auto-dismiss mic errors after 5 seconds
	const [visibleError, setVisibleError] = React.useState<string | null>(null);
	useEffect(() => {
		if (error) {
			setVisibleError(error);
			const id = setTimeout(() => setVisibleError(null), 5000);
			return () => clearTimeout(id);
		}
	}, [error]);

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			onSubmit();
		}
	};

	const micDisabled = disabled || isTranscribing;

	return (
		<Box>
			<Paper elevation={2} sx={{ p: 1 }}>
				<Box sx={{ display: 'flex', gap: 0, alignItems: 'flex-end' }}>
					<TextField
						fullWidth
						multiline
						maxRows={15}
						value={value}
						onChange={(e) => onChange(e.target.value)}
						onKeyPress={handleKeyPress}
						placeholder={placeholder}
						variant="outlined"
						size="small"
						disabled={disabled}
					/>

					{isSupported && (
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							{isTranscribing ? (
								<Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
									<CircularProgress size={20} />
								</Box>
							) : isRecording ? (
								<IconButton
									onClick={stopRecording}
									disabled={micDisabled}
									sx={{
										p: 1,
										color: 'error.main',
										animation: 'pulse 1.2s ease-in-out infinite',
										'@keyframes pulse': {
											'0%, 100%': { opacity: 1 },
											'50%': { opacity: 0.4 },
										},
									}}
									aria-label="Stop recording"
								>
									<StopIcon />
								</IconButton>
							) : (
								<IconButton
									onClick={startRecording}
									disabled={micDisabled}
									sx={{ p: 1 }}
									aria-label="Start voice input"
								>
									<MicIcon />
								</IconButton>
							)}
						</Box>
					)}

					<IconButton
						color="primary"
						onClick={onSubmit}
						disabled={!value.trim() || disabled}
						sx={{ p: 1 }}
					>
						<SendIcon />
					</IconButton>
				</Box>
			</Paper>

			{visibleError && (
				<Typography
					variant="caption"
					sx={{ color: 'error.main', mt: 0.5, display: 'block', px: 1 }}
				>
					{visibleError}
				</Typography>
			)}
		</Box>
	);
};
